import { useState } from 'react';
import { useDynamicDrawerContext } from '../../../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../../../context/snackbar.context';
import {
  GetBillsDocument,
  useCreateManualHoldMutation,
} from '../../../../types';
import {
  ActionTypeDisplayText,
  GridLevelName,
} from '../../../grid-components/grid-types';
import { PanelAlert } from '../../../grid-components/panel-alert';
import { PanelLayout } from '../../../grid-components/panel-layout';
import { PanelTitle } from '../../../grid-components/panel-title';
import { noteInvalid } from '../../../grid-components/panel-validation';
import { BillTypeDataList } from '../../bill-grid-components/bill-management-types';
import { BillPanelProps } from '../panel-types';
import { SelectedBillsGrid } from './selected-grid';

export const CreateManualHoldPanel = ({
  selectedRows,
  clients,
  insurers,
  onClearSelections,
}: BillPanelProps): JSX.Element => {
  const { setIsOpen, setTitle } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();
  const [panelSelectedRows, setPanelSelectedRows] =
    useState<BillTypeDataList>(selectedRows);
  const [note, setNote] = useState<string>('');
  const [createManualHold, { loading }] = useCreateManualHoldMutation();

  const handleManualHold = async (): Promise<void> => {
    try {
      const result = await createManualHold({
        variables: {
          input: {
            billIds: panelSelectedRows.map(row => row.billId),
            note,
          },
        },
        refetchQueries: [GetBillsDocument],
      });
      setSnackbarState(
        result.data?.createManualHold?.details ??
          'Successfully created manual hold',
        'success',
        5000,
      );
      onClearSelections();
      setTimeout(() => setIsOpen(false), 500);
    } catch (e) {
      setSnackbarState(
        `Failed to create manual hold with error: ${e instanceof Error ? e.message : 'Unknown error'}`,
        'error',
        5000,
      );
    }
  };

  const handleSelectionChanged = (selectedGridRows: BillTypeDataList): void => {
    setPanelSelectedRows(selectedGridRows);
    setTitle(
      <PanelTitle
        actionType={ActionTypeDisplayText.CREATE_MANUAL_HOLD}
        rowCount={selectedGridRows.length}
        gridLevel={GridLevelName.BILL}
      />,
    );
  };

  return (
    <PanelLayout
      selectedGrid={
        <SelectedBillsGrid
          selectedRows={panelSelectedRows}
          setPanelSelectedRows={handleSelectionChanged}
          clients={clients}
          insurers={insurers}
        />
      }
      alert={<PanelAlert drawerMode="create-manual-hold" />}
      note={note}
      setNote={setNote}
      setIsOpen={setIsOpen}
      isSubmitting={loading}
      isInvalid={noteInvalid(note)}
      actionLabel="Create Manual Hold"
      handleSubmit={handleManualHold}
    />
  );
};
